/**
 * OnboardingEvidence: one sourced number per wizard step.
 *
 * Quiet by design: a single line under the step description, so it reads as a
 * footnote rather than a claim. The figure is `tabular-nums` in full
 * foreground; the source sits a tone lighter than the sentence that earns it.
 *
 * Contrast floor: the sentence is `text-foreground/80` and the source
 * `text-foreground/70`, not `text-muted-foreground`, which measures 4.48:1 on
 * this canvas and fails WCAG 2.2 AA at this size.
 *
 * Only the tracker step carries one now (2026-09-10, founder: simplify the
 * copy); the plan step shows published examples instead. Do not invent
 * additional numbers: every figure here is traceable to a named study or a
 * named customer, and an unsourced one discredits the rest.
 */

import { Fragment } from "react"
import { cn } from "@/lib/utils"

export type OnboardingEvidenceStep = "install-script"

/** A run of copy. `tone` picks the register; the default is plain body. */
type EvidenceToken = {
  text: string
  tone?: "figure" | "source"
}

const EVIDENCE: Record<OnboardingEvidenceStep, EvidenceToken[]> = {
  "install-script": [
    { text: "Nothing is measured until the script is live. Social See went from " },
    { text: "2% to 8%", tone: "figure" },
    { text: " booking conversion in three months." },
  ],
}

type OnboardingEvidenceProps = {
  step: OnboardingEvidenceStep
  className?: string
}

export function OnboardingEvidence({ step, className }: OnboardingEvidenceProps) {
  const tokens = EVIDENCE[step]
  if (!tokens) return null

  return (
    <p className={cn("text-xs leading-snug text-foreground/80", className)}>
      {tokens.map((token, i) => (
        <Fragment key={i}>
          {token.tone === "figure" ? (
            <span className="tabular-nums font-medium text-foreground">{token.text}</span>
          ) : token.tone === "source" ? (
            <span className="text-foreground/70">{token.text}</span>
          ) : (
            token.text
          )}
        </Fragment>
      ))}
    </p>
  )
}
